// EmpLeaves.js
import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Table,
  Spin,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Space,
  Tag,
  Button,
  Timeline,
  Badge,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { NavigationContext } from "../../context/NavigationContext";
import EmployeeProfile from "../EmployeeProfile";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Map each tab key to the statuses it should fetch
const TAB_STATUSES = {
  Pending: ["Pending Approval", "Pending Cancellation", "Pending"],
  Approved: ["Approved", "Cancellation Approved", "Approved"],
  Rejected: ["Rejected", "Cancellation Rejected"],
};

// Fixed leave type options (backend LEAVE_TYPE_CHOICES) — no longer derived
// from the currently-loaded page, since that page is now server-filtered.
const LEAVE_TYPE_OPTIONS = [
  { value: "Sick", label: "Sick" },
  { value: "Casual", label: "Casual" },
  { value: "Earned", label: "Earned" },
  { value: "Compoff", label: "Compoff" },
  { value: "Lop", label: "NLB" },
  { value: "Weekoff", label: "Weekoff" },
];

export default function EmpLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authData, backUrl } = useContext(AuthContext);

  const [selectedId, setSelectedId] = useState(null);
  const [showEmp, setShowEmp] = useState(false);

  const [Organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });

  // ── Tab counts (badges shown over Pending / Approved / Rejected) ──
  const [tabCounts, setTabCounts] = useState({
    Pending: 0,
    Approved: 0,
    Rejected: 0,
  });

  // Active tab: "Pending" | "Approved" | "Rejected"
  const [activeStatusTab, setActiveStatusTab] = useState("Pending");

  const onBack = () => setShowEmp(false);

  const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

  const [isMyTurnOnly, setIsMyTurnOnly] = useState(false);

  useEffect(() => {
    if (navigationParams?.status) {
      setActiveStatusTab(navigationParams.status);
    }
    if (navigationParams?.my_turn !== undefined) {
      setIsMyTurnOnly(Boolean(navigationParams.my_turn));
    }
    if (navigationParams?.status || navigationParams?.my_turn !== undefined) {
      clearNavigationParams();
    }
  }, [navigationParams, clearNavigationParams]);

  // Filters state — ALL of these are now sent to the backend
  const [filters, setFilters] = useState({
    name: "",
    emp_code: "",
    leave_type: "",
    dateRange: [],
    organisation: "",
    department: "",
    designation: "",
  });

  // Debounced copies of the free-text filters (name / emp_code), so we don't
  // fire an API call on every keystroke.
  const [debouncedText, setDebouncedText] = useState({ name: "", emp_code: "" });

  // ── Approve Modal ──
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLeave, setApproveLeave] = useState(null);
  const [approveRemarks, setApproveRemarks] = useState("");
  const [approving, setApproving] = useState(false);

  // ── Reject Modal ──
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectLeave, setRejectLeave] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  // Bulk Action States
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // ---------- Helper accessors ----------
  const getEmployeeFieldId = (employee, fieldBase) => {
    if (!employee) return null;
    if (employee[fieldBase] !== undefined && employee[fieldBase] !== null)
      return employee[fieldBase];
    const alt = `${fieldBase}_id`;
    if (employee[alt] !== undefined && employee[alt] !== null)
      return employee[alt];
    return null;
  };

  const getEmployeeFieldName = (employee, nameFieldCandidates = []) => {
    if (!employee) return "";
    for (const k of nameFieldCandidates) {
      if (employee[k]) return employee[k];
    }
    return "";
  };

  // ---------- Fetch organisations / departments / designations ----------
  const fetchOrganisation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load organisations");
    }
  };

  const fetchDepartments = async (organisationId) => {
    if (!organisationId) {
      setDepartments([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${organisationId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDepartments(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load departments");
    }
  };

  const fetchDesignations = async (departmentId) => {
    if (!departmentId) {
      setDesignations([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${departmentId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDesignations(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load designations");
    }
  };

  // ---------- Core fetch: ALL filters now sent to the backend ----------
  const fetchLeavesByStatuses = async (
    page = 1,
    statuses = [],
    opts = {}
  ) => {
    const {
      dateRange = [],
      orgFilter = "",
      deptFilter = "",
      desigFilter = "",
      nameFilter = "",
      codeFilter = "",
      leaveTypeFilter = "",
    } = opts;

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("is_employee_view", "false");
      if (isMyTurnOnly && activeStatusTab === "Pending") {
        params.append("my_turn", "true");
      }

      if (statuses.length > 0) {
        params.append("status", statuses.join(","));
      }

      if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        const startStr = dayjs(dateRange[0]).format("YYYY-MM-DD");
        const endStr = dayjs(dateRange[1]).format("YYYY-MM-DD");
        params.append("from_date", startStr);
        params.append("to_date", endStr);
      }

      if (orgFilter) params.append("organisation", orgFilter);
      if (deptFilter) params.append("department", deptFilter);
      if (desigFilter) params.append("designation", desigFilter);
      if (nameFilter) params.append("emp_name", nameFilter);
      if (codeFilter) params.append("emp_code", codeFilter);
      if (leaveTypeFilter) params.append("leave_type", leaveTypeFilter);

      const res = await axios.get(
        `${backUrl}/api/hr/leaves/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );

      const data = res.data?.results ?? [];
      setLeaves(data);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: res.data?.count ?? data.length,
      }));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load leave data");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fetch counts for the Pending / Approved / Rejected badges ----------
  // Counts respect ALL currently active filters.
  const fetchTabCounts = async (opts = {}) => {
    const {
      dateRange = [],
      orgFilter = "",
      deptFilter = "",
      desigFilter = "",
      nameFilter = "",
      codeFilter = "",
      leaveTypeFilter = "",
    } = opts;

    try {
      const entries = Object.entries(TAB_STATUSES); // [ [key, statuses], ... ]
      const results = await Promise.all(
        entries.map(([key, statuses]) => {
          const params = new URLSearchParams();
          params.append("page", 1);
          params.append("is_employee_view", "false");
          if (isMyTurnOnly && key === "Pending") {
            params.append("my_turn", "true");
          }
          if (statuses.length > 0) {
            params.append("status", statuses.join(","));
          }
          if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
            params.append("from_date", dayjs(dateRange[0]).format("YYYY-MM-DD"));
            params.append("to_date", dayjs(dateRange[1]).format("YYYY-MM-DD"));
          }
          if (orgFilter) params.append("organisation", orgFilter);
          if (deptFilter) params.append("department", deptFilter);
          if (desigFilter) params.append("designation", desigFilter);
          if (nameFilter) params.append("emp_name", nameFilter);
          if (codeFilter) params.append("emp_code", codeFilter);
          if (leaveTypeFilter) params.append("leave_type", leaveTypeFilter);

          return axios.get(
            `${backUrl}/api/hr/leaves/?${params.toString()}`,
            { headers: { Authorization: `Bearer ${authData?.access}` } }
          );
        })
      );

      const counts = {};
      entries.forEach(([key], idx) => {
        counts[key] = results[idx]?.data?.count ?? (results[idx]?.data?.results?.length || 0);
      });
      setTabCounts((prev) => ({ ...prev, ...counts }));
    } catch (error) {
      console.error("Error fetching tab counts:", error);
    }
  };

  // ---------- Build the current filter payload (uses debounced text values) ----------
  const buildFilterOpts = () => ({
    dateRange: filters.dateRange,
    orgFilter: filters.organisation,
    deptFilter: filters.department,
    desigFilter: filters.designation,
    nameFilter: debouncedText.name,
    codeFilter: debouncedText.emp_code,
    leaveTypeFilter: filters.leave_type,
  });

  // ---------- Refresh with current tab + current filters ----------
  const refreshData = () => {
    const opts = buildFilterOpts();
    fetchLeavesByStatuses(pagination.current, TAB_STATUSES[activeStatusTab], opts);
    fetchTabCounts(opts);
  };

  // ---------- Effects ----------
  useEffect(() => {
    fetchOrganisation();
    fetchLeavesByStatuses(1, TAB_STATUSES["Pending"], {});
    fetchTabCounts();
  }, []);

  useEffect(() => {
    if (filters.organisation) {
      fetchDepartments(filters.organisation);
    } else {
      setDepartments([]);
      setDesignations([]);
      setFilters((prev) => ({ ...prev, department: "", designation: "" }));
    }
  }, [filters.organisation]);

  useEffect(() => {
    if (filters.department) {
      fetchDesignations(filters.department);
    } else {
      setDesignations([]);
      setFilters((prev) => ({ ...prev, designation: "" }));
    }
  }, [filters.department]);

  // ---------- Debounce the free-text inputs (name / emp_code) ----------
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedText({ name: filters.name, emp_code: filters.emp_code });
    }, 500);
    return () => clearTimeout(handle);
  }, [filters.name, filters.emp_code]);

  // ---------- Refetch from backend whenever ANY filter changes ----------
  useEffect(() => {
    // Only refetch on date range when we have either a complete range or it was cleared
    const isComplete = filters.dateRange?.length === 2 && filters.dateRange[0] && filters.dateRange[1];
    const isCleared = !filters.dateRange || filters.dateRange.length === 0;

    if (isComplete || isCleared) {
      const opts = buildFilterOpts();
      fetchLeavesByStatuses(1, TAB_STATUSES[activeStatusTab], opts);
      fetchTabCounts(opts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.dateRange,
    filters.organisation,
    filters.department,
    filters.designation,
    filters.leave_type,
    debouncedText.name,
    debouncedText.emp_code,
    isMyTurnOnly,
  ]);

  // ---------- Tab click handler ----------
  const handleTabClick = (tabKey) => {
    setActiveStatusTab(tabKey);
    setSelectedRowKeys([]);
    fetchLeavesByStatuses(1, TAB_STATUSES[tabKey], buildFilterOpts());
  };

  // ---------- Approve handler ----------
  const handleApprove = async () => {
    if (!approveLeave) return;
    setApproving(true);
    try {
      await axios.post(
        `${backUrl}/api/hr/leaves/approve/`,
        { leave_ids: [approveLeave.id], approver_reason: approveRemarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success("Leave approved successfully.");
      setShowApproveModal(false);
      setApproveLeave(null);
      setApproveRemarks("");
      refreshData();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Approval failed";
      toast.error(errorMsg);
    } finally {
      setApproving(false);
    }
  };

  // ---------- Reject handler ----------
  const handleReject = async () => {
    if (!rejectLeave) return;
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }
    setRejecting(true);
    try {
      await axios.post(
        `${backUrl}/api/hr/leaves/reject/`,
        { leave_ids: [rejectLeave.id], rejection_reason: rejectReason },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success("Leave rejected successfully.");
      setShowRejectModal(false);
      setRejectLeave(null);
      setRejectReason("");
      refreshData();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Rejection failed";
      toast.error(errorMsg);
    } finally {
      setRejecting(false);
    }
  };

  // ---------- Bulk Approve ----------
  const handleBulkApprove = async () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }
    setBulkProcessing(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/hr/leaves/approve/`,
        { leave_ids: selectedRowKeys, approver_reason: bulkRemarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success(
        res?.data?.message || res?.data?.detail || "Leaves approved successfully"
      );
      setBulkRemarks("");
      setShowBulkApprove(false);
      setSelectedRowKeys([]);
      refreshData();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Bulk approval failed"
      );
    } finally {
      setBulkProcessing(false);
    }
  };

  // ---------- Bulk Reject ----------
  const handleBulkReject = async () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }
    if (!bulkRemarks.trim()) {
      toast.warning("Please provide a reason for rejection");
      return;
    }
    setBulkProcessing(true);
    try {
      const res = await axios.post(
        `${backUrl}/api/hr/leaves/reject/`,
        { leave_ids: selectedRowKeys, rejection_reason: bulkRemarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success(
        res?.data?.message || res?.data?.detail || "Leaves rejected successfully"
      );
      setBulkRemarks("");
      setShowBulkReject(false);
      setSelectedRowKeys([]);
      refreshData();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Bulk rejection failed"
      );
    } finally {
      setBulkProcessing(false);
    }
  };

  // ---------- Excel Download ----------
  const downloadExcel = () => {
    const rows = leaves.map((item) => ({
      "Employee Name": `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`.trim(),
      "Employee Code": item?.employee?.emp_code ?? "-",
      Designation:
        getEmployeeFieldName(item.employee, [
          "designation_name",
          "designation",
        ]) ||
        getEmployeeFieldId(item.employee, "designation") ||
        "-",
      "Leave Type": item.leave_type ?? "-",
      Duration: item.duration_type ?? "-",
      Days: item.leave_days_count ?? "-",
      "From Date": item.start_date
        ? new Date(item.start_date).toLocaleDateString("en-IN")
        : "-",
      "To Date": item.end_date
        ? new Date(item.end_date).toLocaleDateString("en-IN")
        : "-",
      Reason: item.reason ?? "-",
      Status: item.status ?? "-",
      Organisation:
        getEmployeeFieldName(item.employee, [
          "organisation_name",
          "organisation",
        ]) ||
        getEmployeeFieldId(item.employee, "organisation") ||
        "-",
      Department:
        getEmployeeFieldName(item.employee, [
          "department_name",
          "department",
        ]) ||
        getEmployeeFieldId(item.employee, "department") ||
        "-",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Leaves");
    XLSX.writeFile(workbook, `${activeStatusTab}_Leaves.xlsx`);
  };

  // ---------- Table pagination change ----------
  const handleTableChange = (pag) => {
    const newPage = pag.current;
    setPagination((prev) => ({ ...prev, current: newPage }));
    fetchLeavesByStatuses(newPage, TAB_STATUSES[activeStatusTab], buildFilterOpts());
  };

  // ---------- Row Selection ----------
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
    getCheckboxProps: (record) => ({ name: record.id }),
  };

  // ---------- Table columns ----------
  const isHR = authData?.employee?.role !== 2;

  const columns = [
    {
      title: "Employee Code",
      dataIndex: ["employee", "emp_code"],
      key: "emp_code",
      render: (text, record) => (
        <a
          onClick={() => {
            setSelectedId(record.employee.id);
            setShowEmp(true);
          }}
          style={{ cursor: "pointer", color: "#1890ff" }}
        >
          {text}
        </a>
      ),
      width: 180,
    },
    {
      title: "Employee Name",
      key: "name",
      fixed: "left",
      render: (_, record) =>
        `${record?.employee?.first_name ?? ""} ${record?.employee?.last_name ?? ""}`.trim(),
    },
    isHR && {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            shape="circle"
            icon={<CheckOutlined />}
            style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
            onClick={() => {
              setApproveLeave(record);
              setApproveRemarks("");
              setShowApproveModal(true);
            }}
          />
          <Button
            danger
            type="primary"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={() => {
              setRejectLeave(record);
              setRejectReason("");
              setShowRejectModal(true);
            }}
          />
        </Space>
      ),
    },
    {
      title: "Designation",
      key: "designation_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, [
          "designation_name",
          "designation",
        ]) ||
        getEmployeeFieldId(record.employee, "designation") ||
        "-",
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
      render: (text, record) => (
        <>
          <p>{record.leave_type === "Lop" ? "NLB" : text}</p>
          {(record.leave_type === "Compoff" || record.leave_type === "Weekoff") &&
            record.work_dates?.length > 0 && (
              <div style={{ marginTop: "4px", fontSize: "12px", color: "#1890ff" }}>
                <strong>Work Dates:</strong> {record.work_dates.join(", ")}
              </div>
            )}
        </>
      ),
    },
    { title: "Duration", dataIndex: "duration_type", key: "duration_type" },
    { title: "Days", dataIndex: "leave_days_count", key: "leave_days_count" },
    {
      title: "From Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-IN") : "-",
    },
    {
      title: "To Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-IN") : "-",
    },
    { title: "Reason", dataIndex: "reason", key: "reason" },
    {
      title: "Approval Hierarchy",
      key: "remarks",
      width: 400,
      render: (_, record) => {
        const approvals = record.approvals || [];
        if (!approvals.length) return "-";
        return (
          <Timeline style={{ marginTop: "20px" }}>
            {approvals.map((a, index) => {
              const statusColor =
                a.status === "Approved" || a.status === "Cancellation Approved"
                  ? "green"
                  : a.status === "Rejected" || a.status === "Cancellation Rejected"
                    ? "red"
                    : "gray";

              return (
                <Timeline.Item key={index} color={statusColor}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <Text strong style={{ fontSize: "14px" }}>
                          {a.approver_name ||
                            (a.approver?.first_name || a.approver?.last_name
                              ? `${a.approver.first_name || ""} ${a.approver.last_name || ""}`
                              : (a.approver?.user?.first_name || a.approver?.user?.last_name
                                ? `${a.approver.user.first_name || ""} ${a.approver.user.last_name || ""}`
                                : (a.approver?.user?.username || "-"))).trim()}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          {a.approver?.designation_name || "Approver"}
                        </Text>
                      </div>
                      <Tag color={statusColor}>{a.status === "Cancelled" ? "Skipped (Leave Cancelled)" : a.status}</Tag>
                    </div>

                    <div style={{ marginTop: "6px" }}>
                      {(a.approved_on || a.rejected_on || a.approver_reason || a.rejection_reason) && (
                        <div style={{ marginBottom: "8px" }}>
                          {(a.approved_on || a.rejected_on) && (
                            <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                              <Text strong style={{ fontSize: "11px", color: "#595959" }}>{a.rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                              {dayjs(a.approved_on || a.rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                            </div>
                          )}
                          {(a.approver_reason || a.rejection_reason) && (
                            <div style={{ fontSize: "13px" }}>
                              <Text strong>{a.rejection_reason ? "Rejection Remark: " : "Remark: "}</Text>
                              <Text>{a.rejection_reason || a.approver_reason}</Text>
                            </div>
                          )}
                        </div>
                      )}

                      {(a.cancellation_approved_on || a.cancellation_rejected_on || a.cancellation_reason || a.cancellation_rejected_reason) && (
                        <div style={{
                          marginTop: (a.approved_on || a.rejected_on) ? "8px" : "0",
                          paddingTop: (a.approved_on || a.rejected_on) ? "8px" : "0",
                          borderTop: (a.approved_on || a.rejected_on) ? "1px dashed #e8e8e8" : "none"
                        }}>
                          {(a.cancellation_approved_on || a.cancellation_rejected_on) && (
                            <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                              <Text strong style={{ fontSize: "11px", color: "#595959" }}>Cancellation {a.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                              {dayjs(a.cancellation_approved_on || a.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                            </div>
                          )}
                          {(a.cancellation_reason || a.cancellation_rejected_reason) && (
                            <div style={{ fontSize: "13px" }}>
                              <Text strong>Cancellation Remark: </Text>
                              <Text>{a.cancellation_rejected_reason || a.cancellation_reason}</Text>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "Approved" || status === "Cancellation Approved"
            ? "green"
            : status === "Rejected" || status === "Cancellation Rejected"
              ? "red"
              : status === "Cancelled"
                ? "gray"
                : "#faad14";
        return (
          <span style={{ color, fontWeight: 600 }}>{status ?? "-"}</span>
        );
      },
    },
    {
      title: "Organisation",
      key: "organisation_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, [
          "organisation_name",
          "organisation",
        ]) ||
        getEmployeeFieldId(record.employee, "organisation") ||
        "-",
    },
    {
      title: "Department",
      key: "department_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, [
          "department_name",
          "department",
        ]) ||
        getEmployeeFieldId(record.employee, "department") ||
        "-",
    },
  ].filter(Boolean);

  // ---------- Tab config ----------
  const tabs = [
    { key: "Pending", label: "Pending", activeColor: "#faad14" },
    { key: "Approved", label: "Approved", activeColor: "#4CAF50" },
    { key: "Rejected", label: "Rejected", activeColor: "#f5222d" },
  ];

  // ---------- Render ----------
  return (
    <div style={{ padding: "20px" }}>
      {showEmp ? (
        <EmployeeProfile
          empId={selectedId}
          onBack={onBack}
          DisableEditOption={true}
        />
      ) : (
        <>
          <ToastContainer />
          <Title
            level={4}
            style={{
              marginBottom: 16,
              textTransform: "uppercase",
              color: "#0e497b",
            }}
          >
            Employee Leave Records
          </Title>

          {/* Status Tab Buttons with count badges */}
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            {tabs.map((tab) => {
              const isActive = activeStatusTab === tab.key;
              const count = tabCounts[tab.key] ?? 0;
              return (
                <Badge
                  key={tab.key}
                  count={count}
                  overflowCount={999}
                  style={{
                    backgroundColor: isActive ? tab.activeColor : "#d9d9d9",
                    color: isActive ? "#fff" : "#595959",
                    fontWeight: 600,
                    boxShadow: "none",
                    marginRight: "25px",
                  }}
                >
                  <Button
                    type={isActive ? "primary" : "default"}
                    onClick={() => handleTabClick(tab.key)}
                    style={{
                      marginRight: "10px",
                      backgroundColor: isActive ? tab.activeColor : undefined,
                      borderColor: isActive ? tab.activeColor : undefined,
                      color: isActive ? "#fff" : undefined,
                    }}
                  >
                    {tab.label}
                  </Button>
                </Badge>
              );
            })}
          </div>

          {/* Filter Section */}
          <div
            style={{
              background: "#ffffff",
              padding: "15px",
              borderRadius: "10px",
              marginBottom: "15px",
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Employee Name"
                  value={filters.name}
                  onChange={(e) =>
                    setFilters({ ...filters, name: e.target.value })
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Employee Code"
                  value={filters.emp_code}
                  onChange={(e) =>
                    setFilters({ ...filters, emp_code: e.target.value })
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Leave Type"
                  value={filters.leave_type || undefined}
                  onChange={(value) =>
                    setFilters({ ...filters, leave_type: value || "" })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {LEAVE_TYPE_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  format="DD/MM/YYYY"
                  value={filters.dateRange}
                  onChange={(dates) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateRange: dates ? [...dates] : [],
                    }))
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Organisation"
                  value={filters.organisation || undefined}
                  onChange={(value) =>
                    setFilters({
                      ...filters,
                      organisation: value || "",
                      department: "",
                      designation: "",
                    })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {Organisations.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name ?? String(org.id)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Department"
                  value={filters.department || undefined}
                  onChange={(value) =>
                    setFilters({ ...filters, department: value || "", designation: "" })
                  }
                  allowClear
                  style={{ width: "100%" }}
                  disabled={!filters.organisation}
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name ?? String(dept.id)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Designation"
                  value={filters.designation || undefined}
                  onChange={(value) =>
                    setFilters({ ...filters, designation: value || "" })
                  }
                  allowClear
                  style={{ width: "100%" }}
                  disabled={!filters.department}
                >
                  {designations.map((des) => (
                    <Option key={des.id} value={des.id}>
                      {des.name ?? String(des.id)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  type="primary"
                  style={{
                    background: "#0e497b",
                    borderColor: "#0e497b",
                    width: "100%",
                  }}
                  onClick={downloadExcel}
                >
                  Download Excel
                </Button>
              </Col>
            </Row>
          </div>

          {authData?.employee?.is_superuser && (
            <>
              {selectedRowKeys.length > 0 && (
                <div style={{ marginBottom: 16, textAlign: "center" }}>
                  <Space>
                    <Tag color="blue">{selectedRowKeys.length} selected</Tag>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                      onClick={() => setShowBulkApprove(true)}
                    >
                      Bulk Approve
                    </Button>
                    <Button
                      danger
                      type="primary"
                      icon={<CloseOutlined />}
                      onClick={() => setShowBulkReject(true)}
                    >
                      Bulk Reject
                    </Button>
                    <Button onClick={() => setSelectedRowKeys([])}>
                      Clear Selection
                    </Button>
                  </Space>
                </div>
              )}
            </>
          )}

          {/* Table */}
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={columns}
                dataSource={leaves}
                rowKey={(record) => record.id}
                rowSelection={rowSelection}
                bordered
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  pageSize: 25,
                  showSizeChanger: false,
                }}
                onChange={handleTableChange}
                scroll={{ x: "max-content" }}
                components={{
                  header: {
                    cell: (props) => (
                      <th
                        {...props}
                        style={{
                          backgroundColor: "#0e497b",
                          color: "white",
                          marginTop: "20px",
                        }}
                      >
                        {props.children}
                      </th>
                    ),
                  },
                }}
              />
            </div>
          )}

          {/* ── Approve Modal ── */}
          <Modal
            title="Approval Remarks"
            open={showApproveModal}
            footer={null}
            onCancel={() => {
              setShowApproveModal(false);
              setApproveLeave(null);
            }}
            width={600}
            centered
          >
            <p>
              <strong>Employee:</strong> {approveLeave?.employee?.first_name}{" "}
              {approveLeave?.employee?.last_name} (
              {approveLeave?.employee?.emp_code})
            </p>
            <Input.TextArea
              rows={4}
              placeholder="Enter remarks here (optional)..."
              value={approveRemarks}
              onChange={(e) => setApproveRemarks(e.target.value)}
              style={{ borderRadius: "8px", marginBottom: "16px" }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowApproveModal(false);
                  setApproveLeave(null);
                }}
                disabled={approving}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={approving}
                disabled={approving}
                style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                onClick={handleApprove}
              >
                {approving ? "Approving..." : "Approve"}
              </Button>
            </div>
          </Modal>

          {/* ── Reject Modal ── */}
          <Modal
            title="Reason for Rejection"
            open={showRejectModal}
            footer={null}
            onCancel={() => {
              setShowRejectModal(false);
              setRejectLeave(null);
            }}
            width={600}
            centered
          >
            <p>
              <strong>Employee:</strong> {rejectLeave?.employee?.first_name}{" "}
              {rejectLeave?.employee?.last_name} (
              {rejectLeave?.employee?.emp_code})
            </p>
            <Input.TextArea
              rows={4}
              placeholder="Enter reason here..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              style={{
                borderRadius: "8px",
                marginBottom: "16px",
                borderColor: rejectReason.trim() ? "#d9d9d9" : "red",
              }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectLeave(null);
                }}
                disabled={rejecting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                loading={rejecting}
                disabled={rejecting}
                onClick={handleReject}
              >
                {rejecting ? "Rejecting..." : "Reject"}
              </Button>
            </div>
          </Modal>

          {/* ── Bulk Approve Modal ── */}
          <Modal
            title={`Bulk Approve (${selectedRowKeys.length} leaves)`}
            open={showBulkApprove}
            footer={null}
            onCancel={() => {
              setShowBulkApprove(false);
              setBulkRemarks("");
            }}
            width={600}
            centered
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter common remarks for all selected leaves (optional)..."
              value={bulkRemarks}
              onChange={(e) => setBulkRemarks(e.target.value)}
              style={{ borderRadius: "8px", marginBottom: "16px" }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowBulkApprove(false);
                  setBulkRemarks("");
                }}
                disabled={bulkProcessing}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={bulkProcessing}
                disabled={bulkProcessing}
                style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                onClick={handleBulkApprove}
              >
                {bulkProcessing ? "Approving..." : "Approve All"}
              </Button>
            </div>
          </Modal>

          {/* ── Bulk Reject Modal ── */}
          <Modal
            title={`Bulk Reject (${selectedRowKeys.length} leaves)`}
            open={showBulkReject}
            footer={null}
            onCancel={() => {
              setShowBulkReject(false);
              setBulkRemarks("");
            }}
            width={600}
            centered
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter common rejection reason for all selected leaves..."
              value={bulkRemarks}
              onChange={(e) => setBulkRemarks(e.target.value)}
              style={{ borderRadius: "8px", marginBottom: "16px" }}
            />
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => {
                  setShowBulkReject(false);
                  setBulkRemarks("");
                }}
                disabled={bulkProcessing}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                loading={bulkProcessing}
                disabled={bulkProcessing || !bulkRemarks.trim()}
                onClick={handleBulkReject}
              >
                {bulkProcessing ? "Rejecting..." : "Reject All"}
              </Button>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}